// DocComments — threaded review comments for one Documents-library row.
// Loads the flat comment list, rebuilds the reply tree (buildCommentTree),
// and renders each node with a reply composer + delete. A top-level
// composer at the bottom adds a new root comment.

import { useMemo, useState } from 'react'
import { CornerDownRight, Loader2, MessageSquare, Send, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import {
  useCreateDocCommentMutation,
  useDeleteDocCommentMutation,
  useListDocCommentsQuery,
  type DocComment
} from '@axilon-apps/domains-ontology-ui-state'

import { Button } from '../../components/ui/button'
import { Textarea } from '../../components/ui/textarea'
import { buildCommentTree, type CommentNode } from './doc-helpers'

function readError(err: unknown, fallback: string): string {
  if (typeof err === 'object' && err !== null) {
    const maybe = err as { data?: { message?: unknown } }
    if (typeof maybe.data?.message === 'string') return maybe.data.message
  }
  return err instanceof Error ? err.message : fallback
}

/** Localized absolute timestamp, e.g. "Jun 15, 2026, 4:12 PM". */
function commentTime(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  })
}

interface ComposerProps {
  placeholder: string
  submitLabel: string
  submitting: boolean
  autoFocus?: boolean
  onSubmit: (body: string) => Promise<void>
  onCancel?: () => void
}

const Composer = ({
  placeholder,
  submitLabel,
  submitting,
  autoFocus,
  onSubmit,
  onCancel
}: ComposerProps) => {
  const [body, setBody] = useState('')
  const trimmed = body.trim()

  const handleSubmit = async (): Promise<void> => {
    if (trimmed === '') return
    await onSubmit(trimmed)
    setBody('')
  }

  return (
    <div className="flex flex-col gap-2">
      <Textarea
        value={body}
        autoFocus={autoFocus}
        placeholder={placeholder}
        disabled={submitting}
        onChange={(e) => setBody(e.target.value)}
        className="min-h-15 text-sm"
      />
      <div className="flex items-center justify-end gap-2">
        {onCancel ? (
          <Button size="sm" variant="ghost" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
        ) : null}
        <Button
          size="sm"
          onClick={() => void handleSubmit()}
          disabled={submitting || trimmed === ''}
        >
          {submitting ? <Loader2 className="animate-spin" aria-hidden /> : <Send aria-hidden />}
          {submitLabel}
        </Button>
      </div>
    </div>
  )
}

interface CommentItemProps {
  node: CommentNode<DocComment>
  busy: boolean
  onReply: (parentCommentId: string, body: string) => Promise<void>
  onDelete: (comment: DocComment) => Promise<void>
}

const CommentItem = ({ node, busy, onReply, onDelete }: CommentItemProps) => {
  const [replying, setReplying] = useState(false)
  const { comment, replies } = node

  return (
    <li className="flex flex-col gap-1.5">
      <div className="rounded-md border border-line px-3 py-2">
        <div className="flex items-center justify-between gap-2">
          <span className="text-12 font-medium text-ink">{comment.author || 'Anonymous'}</span>
          <span className="text-11 text-ink-3">{commentTime(comment.created_at)}</span>
        </div>
        <p className="m-0 mt-1 whitespace-pre-wrap text-sm text-ink-2">{comment.body}</p>
        <div className="mt-1.5 flex items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            className="h-6 px-1.5 text-11"
            onClick={() => setReplying((v) => !v)}
            disabled={busy}
          >
            <CornerDownRight aria-hidden />
            Reply
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-6 px-1.5 text-11 text-st-down"
            aria-label="Delete comment"
            onClick={() => void onDelete(comment)}
            disabled={busy}
          >
            <Trash2 aria-hidden />
          </Button>
        </div>
      </div>

      {replying ? (
        <div className="ml-4">
          <Composer
            autoFocus
            placeholder={`Reply to ${comment.author || 'this comment'}…`}
            submitLabel="Reply"
            submitting={busy}
            onCancel={() => setReplying(false)}
            onSubmit={async (body) => {
              await onReply(comment.id, body)
              setReplying(false)
            }}
          />
        </div>
      ) : null}

      {replies.length > 0 ? (
        <ul className="ml-4 flex flex-col gap-1.5 border-l border-line pl-3">
          {replies.map((child) => (
            <CommentItem
              key={child.comment.id}
              node={child}
              busy={busy}
              onReply={onReply}
              onDelete={onDelete}
            />
          ))}
        </ul>
      ) : null}
    </li>
  )
}

export interface DocCommentsProps {
  docId: string
}

const DocComments = ({ docId }: DocCommentsProps) => {
  const commentsQuery = useListDocCommentsQuery(docId)
  const [createComment, createState] = useCreateDocCommentMutation()
  const [deleteComment, deleteState] = useDeleteDocCommentMutation()

  const comments = commentsQuery.data ?? []
  const tree = useMemo(() => buildCommentTree(comments), [comments])
  const busy = createState.isLoading || deleteState.isLoading

  const handleAdd = async (body: string): Promise<void> => {
    try {
      await createComment({ id: docId, input: { body } }).unwrap()
    } catch (err) {
      toast.error(readError(err, 'Failed to add comment'))
    }
  }

  const handleReply = async (parentCommentId: string, body: string): Promise<void> => {
    try {
      await createComment({
        id: docId,
        input: { body, parent_comment_id: parentCommentId }
      }).unwrap()
    } catch (err) {
      toast.error(readError(err, 'Failed to add reply'))
    }
  }

  const handleDelete = async (comment: DocComment): Promise<void> => {
    try {
      await deleteComment({ docId, commentId: comment.id }).unwrap()
    } catch (err) {
      toast.error(readError(err, 'Failed to delete comment'))
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-1.5 text-11 uppercase tracking-wider text-ink-3">
        <MessageSquare className="size-3" aria-hidden />
        Comments
        {comments.length > 0 ? <span>· {comments.length}</span> : null}
      </div>

      {commentsQuery.isLoading ? (
        <div className="flex items-center gap-2 text-sm text-ink-3">
          <Loader2 className="size-4 animate-spin" aria-hidden />
          Loading comments…
        </div>
      ) : tree.length === 0 ? (
        <p className="m-0 text-sm text-ink-3">No comments yet. Start the review below.</p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {tree.map((node) => (
            <CommentItem
              key={node.comment.id}
              node={node}
              busy={busy}
              onReply={handleReply}
              onDelete={handleDelete}
            />
          ))}
        </ul>
      )}

      <Composer
        placeholder="Add a review comment…"
        submitLabel="Comment"
        submitting={createState.isLoading}
        onSubmit={handleAdd}
      />
    </div>
  )
}

export default DocComments
